import { useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/api/client';
import { useAuth } from '@/hooks/useAuth';

export interface LLMBalance {
  balance: number;
  frozen: number;
  total_deposited: number;
  total_used: number;
  currency: string;
}

const BALANCE_QUERY_KEY = ['llmBalance'];
const BALANCE_INTERVAL_MS = 30000;
const BALANCE_STALE_MS = 30000;

// 桌面端（Electron）本地 /billing/balance 被 require_platform_billing 恒 404
// （本机无平台计费是已知设计），游客更是必 401——这两种情况都不该轮询
const isDesktop = !!window.psbDesktop?.isDesktop;

export function useLLMBalance(_pollIntervalMs = BALANCE_INTERVAL_MS) {
  const queryClient = useQueryClient();
  const { isLoggedIn } = useAuth();

  const { data: balance, isLoading, error, refetch } = useQuery({
    queryKey: BALANCE_QUERY_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get('/api/v1/billing/balance');
      return data as LLMBalance;
    },
    enabled: isLoggedIn && !isDesktop,
    staleTime: BALANCE_STALE_MS,
    refetchInterval: BALANCE_INTERVAL_MS,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const refresh = async () => {
    await refetch();
  };

  const setBalance = (value: LLMBalance) => {
    queryClient.setQueryData(BALANCE_QUERY_KEY, value);
  };

  return {
    balance,
    loading: isLoading,
    error: error ? (error as any)?.response?.data?.detail || (error as Error).message || '加载余额失败' : null,
    refresh,
    setBalance,
  };
}

